import React, { useEffect, useState } from "react";
import { AlertTriangle, Check, Copy, KeyRound, Loader2, Plus, X } from "lucide-react";
import { apiAccessKeys } from "../../utils/api";

const formatDate = (value) => {
  if (!value) return "Never";
  return new Date(value).toLocaleDateString();
};

const AiAccessKeysPanel = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [name, setName] = useState("");
  const [access, setAccess] = useState("read");
  const [expiresInDays, setExpiresInDays] = useState(90);
  const [newToken, setNewToken] = useState("");
  const [copied, setCopied] = useState(false);

  const loadKeys = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await apiAccessKeys.list();
      setKeys(response.data.keys || []);
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
          "Could not load your AI access keys."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadKeys();
  }, []);

  const handleCreate = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");
    setNewToken("");
    setCopied(false);

    try {
      const response = await apiAccessKeys.create({
        name: name.trim(),
        access,
        expiresInDays: Number(expiresInDays),
      });
      setNewToken(response.data.token);
      setSuccess("AI access key created.");
      setName("");
      await loadKeys();
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
          "Could not create the AI access key."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(newToken);
      setCopied(true);
    } catch (copyError) {
      setError("Copy failed. Select the key and copy it manually.");
    }
  };

  const handleRevoke = async (key) => {
    const confirmed = window.confirm(
      `Revoke "${key.name}" now? Any AI using it will lose access immediately.`
    );
    if (!confirmed) return;

    try {
      setError("");
      setSuccess("");
      await apiAccessKeys.revoke(key.id);
      setSuccess(`Revoked "${key.name}".`);
      await loadKeys();
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
          "Could not revoke the AI access key."
      );
    }
  };

  return (
    <div id="ai-access-keys-panel" className="border-t border-gray-200 pt-6">
      <div id="ai-access-keys-heading" className="flex items-start justify-between gap-4">
        <div id="ai-access-keys-heading-copy">
          <h3 className="text-base font-semibold text-gray-900 flex items-center">
            <KeyRound className="w-5 h-5 mr-2 text-brand-primary" />
            AI access keys
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            Give an external AI limited access without sharing your password or browser login.
          </p>
        </div>
      </div>

      {error && (
        <div
          id="api-keys-error"
          className="mt-4 flex items-start rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
          role="alert"
        >
          <AlertTriangle className="mr-2 h-4 w-4 flex-shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      {success && (
        <div
          id="api-keys-success"
          className="mt-4 flex items-start rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700"
          role="status"
        >
          <Check className="mr-2 h-4 w-4 flex-shrink-0 mt-0.5" />
          {success}
        </div>
      )}

      {newToken && (
        <div
          id="api-key-one-time-secret"
          className="mt-4 rounded-lg border-2 border-amber-300 bg-amber-50 p-4"
        >
          <div id="api-key-secret-warning" className="flex items-start justify-between gap-3">
            <div id="api-key-secret-warning-copy">
              <p className="font-semibold text-amber-900">Copy this key now</p>
              <p className="text-sm text-amber-800">
                This is the only time the full key will be shown.
              </p>
            </div>
            <button
              type="button"
              aria-label="Dismiss new API key"
              onClick={() => setNewToken("")}
              className="text-amber-700 hover:text-amber-900"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div id="api-key-secret-copy-row" className="mt-3 flex flex-col gap-2 sm:flex-row">
            <input
              aria-label="New API key"
              readOnly
              value={newToken}
              className="min-w-0 flex-1 rounded-md border border-amber-300 bg-white px-3 py-2 font-mono text-xs"
              onFocus={(event) => event.target.select()}
            />
            <button
              type="button"
              onClick={handleCopy}
              className="btn-brand-primary btn-brand-sm inline-flex items-center justify-center"
            >
              {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
              {copied ? "Copied" : "Copy key"}
            </button>
          </div>
        </div>
      )}

      <form
        id="api-key-create-form"
        onSubmit={handleCreate}
        className="mt-5 grid gap-4 rounded-lg bg-gray-50 p-4 md:grid-cols-4"
      >
        <div id="api-key-name-field" className="md:col-span-2">
          <label htmlFor="api-key-name" className="block text-sm font-medium text-gray-700">
            Key name
          </label>
          <input
            id="api-key-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={80}
            required
            placeholder="Example: Codex on my Mac"
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
        <div id="api-key-access-field">
          <label htmlFor="api-key-access" className="block text-sm font-medium text-gray-700">
            Access
          </label>
          <select
            id="api-key-access"
            value={access}
            onChange={(event) => setAccess(event.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="read">Read only</option>
            <option value="read_write">Read + safe writes</option>
          </select>
        </div>
        <div id="api-key-expiry-field">
          <label htmlFor="api-key-expiry" className="block text-sm font-medium text-gray-700">
            Expires
          </label>
          <select
            id="api-key-expiry"
            value={expiresInDays}
            onChange={(event) => setExpiresInDays(event.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          >
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={180}>180 days</option>
            <option value={365}>1 year</option>
          </select>
        </div>
        <div id="api-key-create-actions" className="md:col-span-4 flex items-center justify-between gap-4">
          <p className="text-xs text-gray-600">
            Safe writes can create, update, and log. Deletes, sending, uploads, and admin actions stay blocked.
          </p>
          <button
            type="submit"
            disabled={saving || !name.trim()}
            className="btn-brand-primary btn-brand-sm inline-flex flex-shrink-0 items-center"
          >
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Plus className="mr-2 h-4 w-4" />
            )}
            Create key
          </button>
        </div>
      </form>

      <div id="api-keys-list" className="mt-5">
        {loading ? (
          <div id="api-keys-loading" className="flex items-center py-4 text-sm text-gray-500">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading keys…
          </div>
        ) : keys.length === 0 ? (
          <div id="api-keys-empty" className="rounded-md border border-dashed border-gray-300 p-4 text-sm text-gray-600">
            No AI access keys yet.
          </div>
        ) : (
          <div id="api-keys-rows" className="space-y-3">
            {keys.map((key) => {
              const inactive = Boolean(key.revokedAt) || new Date(key.expiresAt) <= new Date();
              return (
                <div
                  id={`api-key-row-${key.id}`}
                  key={key.id}
                  className="flex flex-col justify-between gap-3 rounded-lg border border-gray-200 p-4 sm:flex-row sm:items-center"
                >
                  <div id={`api-key-summary-${key.id}`}>
                    <p className="font-medium text-gray-900">{key.name}</p>
                    <p className="mt-1 font-mono text-xs text-gray-500">{key.prefix}…</p>
                    <p className="mt-1 text-xs text-gray-500">
                      {key.access === "read_write" ? "Read + safe writes" : "Read only"}
                      {" · "}Expires {formatDate(key.expiresAt)}
                      {" · "}Last used {formatDate(key.lastUsedAt)}
                    </p>
                  </div>
                  <div id={`api-key-actions-${key.id}`} className="flex items-center gap-3">
                    <span
                      className={`rounded-full px-2 py-1 text-xs font-medium ${
                        inactive
                          ? "bg-gray-100 text-gray-600"
                          : "bg-green-100 text-green-700"
                      }`}
                    >
                      {key.revokedAt ? "Revoked" : inactive ? "Expired" : "Active"}
                    </span>
                    {!inactive && (
                      <button
                        type="button"
                        onClick={() => handleRevoke(key)}
                        className="text-sm font-medium text-red-600 hover:text-red-800"
                      >
                        Revoke
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AiAccessKeysPanel;
